import classNames from "classnames";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";

import {FormattedMessage, useIntl} from "react-intl";
import LoadingSpinner from "../../atoms/loadingSpinner/LoadingSpinner.jsx";
import {GlobalContext} from "../../globals/globalContext";
import {operations} from "../../util/constants";
import {post} from "../../util/fetch";
import {removeDiacritics} from "../../util/script";
import {AutoBuySection} from "./AutoBuySection";
import {FavouriteSearch} from "./FavouriteSearch.jsx";
import {LikedProductsList} from "./LikedProductsList";

import {calculateNextDeliveryDate, parseRRULE} from "../../util/helpers";
import DateSelectionList from "../date-selection-list/DateSelectionList";
import ItemOptionsModal from "../item-options-modal/ItemOptionModal";
import "./styles.styl";

const cleanText = (str) => removeDiacritics(str || "").toLowerCase();

export default function RuleOverView(props) {
  const {
    productType,
    filteredProductType,
    userLikedProducts,
    setProductType,
    setFilteredProductType,
    setUserLikedProducts,
    fetchPreferencesAndRules,
    isLoadingPreferencesAndRules,
    fetchCatalog,
    fetchDelivery,
    subscription,
  } = useContext(GlobalContext);

  const {
    className,
    deliveryName,
    companyName,
    logoSmallUrl,
    displayed,
    onSuccess,
    visitedFromCart,
    onCloseClick,
    delivery,
    companyEmail,
    colorSecondary,
    ...rest
  } = props;
  const intl = useIntl();
  const [focusedOn, setFocusedOn] = useState();
  const [hasSubscribeMode, setHasSubscribeMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const closeSearch = () => setFocusedOn("");
  const isRegistered = subscription?.customer?.is_registered;
  const clearMessageTimeout = useRef(null);

  const [timeslotsBeforeOrdering, setTimeslotsBeforeOrdering] =
    useState(undefined);

  const [dates, setDates] = useState(
    Array(4).fill({ date: "", original_date: "", selected: false }),
  );
  const [isLoadingUpcomingDates, setIsLoadingUpcomingDates] = useState(false);
  const [shakeDateIndex, setShakeDateIndex] = useState(null);
  const [dateSelectionMessage, setDateSelectionMessage] = useState("");
  const [datesError, setDatesError] = useState("");

  useEffect(() => {
    if (!subscription?.id) return;

    const initializeComponent = async () => {
      try {
        setIsLoading(true);

        setUserLikedProducts([]);
        setFilteredProductType([]);
        setDates(Array(4).fill({ date: "", original_date: "", selected: false }));

        const timeslotsResponse = await fetch(
          `/api/subscriptions/${subscription.id}/timeslots-before-ordering-timeframe`
        );
        const timeslotsData = await timeslotsResponse.json();
        const timeslots = timeslotsData.timeslots || [];
        setTimeslotsBeforeOrdering(timeslots);

        await fetchUpcomingDates(false);

        await fetchPreferencesAndRules(subscription.id, true);
      } catch (error) {
        console.error("Error initializing component:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeComponent();
  }, [subscription?.id]);

  useEffect(() => {
    setHasSubscribeMode(subscription.auto_buy);
  }, [subscription.auto_buy]);

  const deliverListing = useMemo(
    () => selectedItem ? { ...selectedItem, id: selectedItem.id } : null,
    [selectedItem]
  );

  const setAsNotSelected = (item) => {
    setProductType((current) =>
      current.map((currentItem) =>
        currentItem.id !== item.id
          ? currentItem
          : { ...currentItem, selected: false },
      ),
    );
  };

  const onSelectLikedProducts = (item) => {
    if (!item.selected) {
      setSelectedItem(item);
      setIsModalOpen(true);
    }
  };

  const handleConfirmModal = (ruleProductData) => {
    const { amount } = ruleProductData;

    const requestData = {
      ...ruleProductData,
      temporary_amount: amount,
      amount: amount,
    };
    delete requestData.is_temporary;

    post("/api/rule-product/", { body: requestData })
      .then(() => {
        setIsModalOpen(false);
        setSelectedItem(null);

        fetchPreferencesAndRules(subscription.id, true);
        fetchCatalog();
        fetchDelivery();
      })
      .catch((error) => {
        console.error(error);
        setIsModalOpen(false);
        setSelectedItem(null);
      });
  };

  const changeUserLikedProductAmount = (item, increment) => {
    setUserLikedProducts((current) =>
      current.map((currentItem) =>
        currentItem === item
          ? {
              ...currentItem,
              temporary_amount: currentItem.temporary_amount + increment,
              amount: isRegistered
                ? currentItem.temporary_amount + increment
                : null,
            }
          : currentItem,
      ),
    );
  };

  const handleQts = (operation, item) => {
    if (operation === operations.add) {
      changeUserLikedProductAmount(item, 1);
    } else if (item.amount === 1) {
      setAsNotSelected(item);
      setUserLikedProducts((current) =>
        current.filter((currentItem) => currentItem !== item),
      );
    } else {
      changeUserLikedProductAmount(item, -1);
    }
  };

  const onSearchLikedProduct = (text, uniqueKey) => {
    if (!focusedOn || uniqueKey !== focusedOn) {
      setFocusedOn(uniqueKey);
    }

    const cleanedText = cleanText(text);
    if (cleanedText.length > 0) {
      setFilteredProductType(
        productType.filter((currentItem) => {
          const articleNameAndQualitiesMatches = cleanText(
            currentItem.article_name_and_qualities,
          ).includes(cleanedText);
          const producerMatches = cleanText(currentItem.producer).includes(
            cleanedText,
          );

          return (
            articleNameAndQualitiesMatches || producerMatches
            // ArticleNameAndQualitiesMatches || productBaseNameMatches || producerMatches
          );
        }),
      );
    } else {
      setFocusedOn("");
    }
  };

  const nextDeliveryDate = useMemo(() => {
    const allDatesEmptyAndDeselected = dates.every(
      (dateInfo) => dateInfo.date === "" && !dateInfo.selected,
    );
    if (allDatesEmptyAndDeselected) {
      return null;
    }

    const currentDate = delivery.delivery_date.split(".").reverse().join("-");
    const parsedRule = parseRRULE(
      subscription.periodicity || "RRULE:FREQ=WEEKLY",
    );

    return calculateNextDeliveryDate(
      currentDate,
      parsedRule,
      delivery.timeframe_short,
      dates,
      intl,
      false,
    );
  }, [delivery.delivery_date, subscription.periodicity, dates]);

  const fetchUpcomingDates = async (displayLoading = true) => {
    if (displayLoading) {
      setIsLoadingUpcomingDates(true);
    }
    try {
      let url = `/api/subscriptions/${subscription.id}/periodicity?delivery_id=${delivery.id}`;

      if (timeslotsBeforeOrdering !== undefined) {
        url += `&timeslots=${encodeURIComponent(
          JSON.stringify(timeslotsBeforeOrdering),
        )}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      const processedDates = (data.dates || []).map((dateInfo) => {
        const paused = !dateInfo.non_zero_amount_items_exist && dateInfo.sold;
        const sold = dateInfo.sold && !paused;
        const selected = paused ? false : dateInfo.selected;

        return {
          ...dateInfo,
          paused,
          sold,
          selected,
        };
      });
      setDates(processedDates);
    } catch (error) {
      console.error("Error fetching subscription periodicity:", error);
    } finally {
      if (displayLoading) {
        setIsLoadingUpcomingDates(false);
      }
    }
  };

  const handleDateClick = async (dateObj, setIsUpdating) => {
    setIsUpdating(true);

    const date = dateObj.date;
    const isGoingToBeSelected = !dateObj.selected;

    const updatedDates = dates.map((dateInfo) =>
      dateInfo.date === date
        ? { ...dateInfo, selected: !dateInfo.selected }
        : dateInfo,
    );

    const parsedRule = parseRRULE(
      subscription.periodicity || "RRULE:FREQ=WEEKLY",
    );
    let periodicityRule;
    if (parsedRule.frequency === "WEEKLY") {
      if (parsedRule.interval === 1) {
        periodicityRule = "RRULE:FREQ=WEEKLY";
      } else {
        periodicityRule = `RRULE:FREQ=${parsedRule.frequency};INTERVAL=${parsedRule.interval}`;
      }
    } else {
      return;
    }

    const { formattedDate, _date } = calculateNextDeliveryDate(
      delivery.delivery_date.split(".").reverse().join("-"),
      parsedRule,
      delivery.timeframe_short,
      updatedDates,
      intl,
      false,
    );

    const hasSameDateAsDelivery = delivery.delivery_date === date;
    let pauseCurrentDelivery = !isGoingToBeSelected && hasSameDateAsDelivery;

    const firstSelectedDateInfo = updatedDates.find(
      (dateInfo) => dateInfo.selected,
    );
    let dtStartString = "";

    if (firstSelectedDateInfo) {
      const [year, month, day] =
        firstSelectedDateInfo.original_date_iso_formatted.split("-");
      dtStartString = `DTSTART:${year}${month}${day}T121212Z\n`;
    } else {
      const [year, month, day] = _date.toISOString().split("T")[0].split("-");
      dtStartString = `DTSTART:${year}${month}${day}T121212Z\n`;
    }
    const periodicity = dtStartString + periodicityRule;

    const datesWithSelection = updatedDates.map((dateInfo) => ({
      date: dateInfo.original_date_iso_formatted,
      selected: dateInfo.selected,
    }));

    const payload = {
      dates_with_selection: datesWithSelection,
      periodicity,
      has_same_date_as_delivery: hasSameDateAsDelivery,
      pause_current_delivery: pauseCurrentDelivery,
      selected_delivery_date: dateObj.date_iso_formatted,
      is_going_to_be_selected: isGoingToBeSelected,
    };

    try {
      const response = await fetch(
        `/api/subscriptions/${subscription.id}/periodicity?delivery_id=${delivery.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update subscription periodicity");
      }

      const data = await response.json();
      setDates(updatedDates);
      if (hasSameDateAsDelivery) {
        fetchDelivery();
      }
      await fetchUpcomingDates(false);

      if (clearMessageTimeout.current) {
        clearTimeout(clearMessageTimeout.current);
      }

      const nextDateHasChanged =
        nextDeliveryDate.formattedDate !== formattedDate;

      let messageId;
      if (hasSameDateAsDelivery && !isGoingToBeSelected) {
        messageId = nextDateHasChanged
          ? "datePausedMessage"
          : "datePausedMessageSameDate";
      } else {
        messageId = isGoingToBeSelected
          ? nextDateHasChanged
            ? "dateSelectedMessage"
            : "dateSelectedMessageSameDate"
          : nextDateHasChanged
          ? "dateDeselectedMessage"
          : "dateDeselectedMessageSameDate";
      }

      setDateSelectionMessage(
        intl.formatMessage(
          { id: messageId },
          { date, nextDelivery: formattedDate },
        ),
      );

      clearMessageTimeout.current = setTimeout(() => {
        setDateSelectionMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error updating subscription periodicity:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div
      className={classNames("rule", className)}
      onClick={closeSearch}
      {...rest}
    >
      {isLoading ? (
        <LoadingSpinner
          isLoading={true}
          clazzName={"rule__spinner"}
          applyStylesToMain={true}
        />
      ) : (
        <>
          <AutoBuySection
            displayed={displayed}
            hasSubscribeMode={hasSubscribeMode}
            companyName={companyName}
            deliveryName={deliveryName}
          />
          <DateSelectionList
            dates={dates}
            handleDateClick={handleDateClick}
            shakeDateIndex={shakeDateIndex}
            dateSelectionMessage={dateSelectionMessage}
            datesError={datesError}
            isLoadingUpcomingDates={isLoadingUpcomingDates}
            onShakeEnd={() => setShakeDateIndex(null)}
            fromSubscriptionManagement={true}
          />
          <div className="delivery-scheduled-container">
            <FormattedMessage
              id="scheduledDelivery"
              values={{
                b: (chunks) => <b>{chunks}</b>,
                date: <b>{nextDeliveryDate?.formattedDate}</b>,
              }}
              tagName="p"
              className="delivery-scheduled-message"
            />
          </div>
          <div>
            <div className="rule__proposal">
              <div className="rule__proposal-header">
                <h3>
                  {" "}
                  <FormattedMessage id="mySubscribedProducts" />{" "}
                </h3>
              </div>
              <div className="rule__proposal_search">
                <FavouriteSearch
                  search={onSearchLikedProduct}
                  placeholder={intl.formatMessage({
                    id: "addProductsToSubscription",
                  })}
                  uniqueKey="first"
                  focusedOn={focusedOn}
                  items={filteredProductType}
                  onSelect={onSelectLikedProducts}
                  isLoading={isLoadingPreferencesAndRules}
                />
                <p className="rule__proposal_search-description">
                  <FormattedMessage id="fillCartDescription" />
                </p>
                <LikedProductsList
                  userLikedProducts={userLikedProducts}
                  handleQts={handleQts}
                  logoSmallUrl={logoSmallUrl}
                  delivery={delivery}
                  fetchPreferencesAndRules={fetchPreferencesAndRules}
                  fetchDelivery={fetchDelivery}
                  fetchCatalog={fetchCatalog}
                  timeslotsBeforeOrdering={timeslotsBeforeOrdering}
                  isLoadingPreferencesAndRules={isLoadingPreferencesAndRules}
                />
              </div>
            </div>
          </div>
        </>
      )}
      {isModalOpen && selectedItem && (
        <ItemOptionsModal
          deliver_listing={deliverListing}
          delivery={delivery}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedItem(null);
          }}
          onConfirm={handleConfirmModal}
          fromSettings={true}
        />
      )}
    </div>
  );
}
